import KeyIcon from '@mui/icons-material/Key'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Box, Card, CardContent, Typography, TextField, Button, Alert, InputAdornment } from '@mui/material'
import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import useBar from '../components/common/Bar'
import AuthService from '../services/AuthService'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [error, setError] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [success, setSuccess] = React.useState(false)
  const [showNewPassword, setShowNewPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)
  const bar = useBar()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    setError('')
    setIsSubmitting(true)

    try {
      await AuthService.updatePassword({ resetToken: token, newPassword })
      setSuccess(true)
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password has been reset successfully. Please login with your new password.' },
        })
      }, 100)
    } catch (error) {
      console.error('Password reset failed:', error)
      setError('Network error. Please try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleTogglePasswordVisibility = (field) => {
    if (field === 'new') {
      setShowNewPassword(!showNewPassword)
    } else {
      setShowConfirmPassword(!showConfirmPassword)
    }
  }

  if (!token) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: '400px', mt: 4 }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom>
              Invalid Reset Link
            </Typography>
            <Alert severity="error">This password reset link is invalid or has expired. Please request a new password reset link.</Alert>
            <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: '400px', mt: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Reset Password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password reset successful! Redirecting to login...
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isSubmitting || success}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showNewPassword ? (
                      <VisibilityOffIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting || success ? 'default' : 'pointer',
                          color: isSubmitting || success ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting || success ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && !success && handleTogglePasswordVisibility('new')}
                      />
                    ) : (
                      <VisibilityIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting || success ? 'default' : 'pointer',
                          color: isSubmitting || success ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting || success ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && !success && handleTogglePasswordVisibility('new')}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isSubmitting || success}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showConfirmPassword ? (
                      <VisibilityOffIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting || success ? 'default' : 'pointer',
                          color: isSubmitting || success ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting || success ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && !success && handleTogglePasswordVisibility('confirm')}
                      />
                    ) : (
                      <VisibilityIcon
                        fontSize="small"
                        sx={{
                          cursor: isSubmitting || success ? 'default' : 'pointer',
                          color: isSubmitting || success ? 'text.disabled' : 'action.active',
                          '&:hover': {
                            color: isSubmitting || success ? 'text.disabled' : 'primary.main',
                          },
                        }}
                        onClick={() => !isSubmitting && !success && handleTogglePasswordVisibility('confirm')}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <Button type="submit" loading={isSubmitting} loadingPosition="start" fullWidth variant="contained" sx={{ mt: 2 }}>
              {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}
